/**
 * "Remove from Lyrixa Catalogue?" confirmation.
 *
 * The reverse of MakeDiscoverableModal, and the reason that one can be offered at all:
 * a listing decision a user cannot take back is not a decision they can make freely.
 * Confirming sets visibility back to `'private'` through the same `setVisibility` seam.
 *
 * IT REMOVES A LISTING, NOT A SONG — which is the whole job of the body copy. The song,
 * its audio, its lyrics, its artwork, its versions and its playlist memberships are all
 * untouched; only `visibility` moves. Sitting one press from a Delete in the same menu,
 * this dialog has to say so plainly, so the sentence names the Library explicitly.
 *
 * NOT `destructive`, deliberately. The red confirm button is for irreversible loss, and
 * this is neither irreversible nor a loss — it is the same reversible listing decision
 * as its counterpart, taken in the other direction. Delete is the destructive dialog.
 */
import { EyeOff } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface RemoveFromCatalogueModalProps {
  /** The song pending removal from the catalogue, or null when closed. */
  song: Song | null;
  onClose: () => void;
  onConfirm: () => void;
}

export default function RemoveFromCatalogueModal({
  song,
  onClose,
  onConfirm,
}: RemoveFromCatalogueModalProps) {
  return (
    <ConfirmDialog
      open={Boolean(song)}
      onClose={onClose}
      title="Remove from Lyrixsa Catalogue?"
      icon={<EyeOff size={19} color={c.accent} />}
      confirmLabel="Remove"
      confirmIcon={<EyeOff size={13} />}
      onConfirm={onConfirm}
    >
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 18px', lineHeight: 1.5 }}>
        "{song?.title}" will no longer appear in the Lyrixsa Catalogue. The song stays in your
        library, with its lyrics, artwork and playlists untouched.
      </p>
    </ConfirmDialog>
  );
}
